import { useState, useCallback, useEffect, useRef } from 'react';
import { sendChatMessage } from '../services/api/chatApi';
import { MESSAGE_ROLES, ERROR_MESSAGES } from '../constants';

/**
 * Custom hook for managing chat functionality
 * CONVERSATION_ID SUPPORT:
 * - First message sent with conversation_id = null → backend creates conversation
 * - Response includes conversation_id → stored in LOCAL state
 * - All subsequent messages use LOCAL conversation_id
 * - When user switches conversation (via sidebar), prop updates and local state resets
 * 
 * CONVERSATION_ID FLOW:
 * 1. New chat: conversationId = null → send message
 * 2. Backend: Creates conversation, returns conversation_id = "uuid"
 * 3. Local state: setConversationId("uuid") 
 * 4. Next message: Uses conversationId = "uuid" (from local state, NOT prop)
 * 5. Switch tab: Prop changes → useEffect clears local state and chatLog
 * 6. New tab: conversationId resets, cycle repeats for new conversation
 * 
 * @param {Array} selectedImages - Currently selected File objects with metadata
 * @param {Function} clearImages - Function to clear selected files
 * @param {Object} agentApi - Agent-specific API (optional, falls back to default)
 * @param {Object} options - Configuration options
 * @param {string} options.transmissionMode - 'json' (base64) or 'multipart' (FormData)
 * @param {string|null} options.conversationId - Conversation ID or null for new chat
 * @param {Function} options.onConversationCreated - Callback when new conversation is created
 * @param {Function} options.onConversationUpdated - Callback when conversation is updated after message
 * @returns {Object} - Chat state and handlers (includes conversationId)
 */
export const useChat = (selectedImages = [], clearImages = () => {}, agentApi = null, options = {}) => {
  const { 
    transmissionMode = 'multipart', 
    conversationId: initialConversationId = null,
    onConversationCreated = () => {},
    onConversationUpdated = () => {}
  } = options;
  const [message, setMessage] = useState('');
  const [chatLog, setChatLog] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sendError, setSendError] = useState(null);
  // LOCAL conversation state: starts from prop, updated when backend returns conversation_id
  const [conversationId, setConversationId] = useState(initialConversationId);
  // Track if we just created a new conversation (to avoid clearing chatLog)
  const justCreatedConversationRef = useRef(false);

  /**
   * Reset conversation when prop changes (user switches tabs)
   * This clears local state to match the newly selected conversation
   * 
   * IMPORTANT: If we just created a conversation (justCreatedConversationRef is true),
   * we should NOT clear chatLog because the conversation_id prop update is from
   * our own onConversationCreated callback, not from user selecting a different conversation
   */
  useEffect(() => {
    // Update local state to new prop value
    setConversationId(initialConversationId);
    
    // Only clear chat log if we're NOT transitioning from null→uuid (new conversation creation)
    // If justCreatedConversationRef is true, it means we just created this conversation
    // so we should NOT clear the chatLog that was just built
    if (!justCreatedConversationRef.current) {
      setChatLog([]);
    } else {
      // Reset flag for next time
      justCreatedConversationRef.current = false;
    }
  }, [initialConversationId]);

  /**
   * Send a message to the chat with actual file contents
   * WHY: Passes File objects to API for binary content transmission with conversation context
   * 
   * CONVERSATION_ID FLOW:
   * 1. Reads current conversationId from local state (via closure)
   * 2. Sends message with current conversationId
   * 3. If conversationId is null, backend creates new conversation
   * 4. Response includes conversation_id, update local state
   * 5. Next send will use the new conversationId
   */
  const sendMessage = useCallback(async () => {
    // WHY: Early return prevents unnecessary processing with empty input
    if (!message.trim() && (!selectedImages || selectedImages.length === 0)) return;
    
    try {
      setSendError(null);
      setIsLoading(true);
      
      console.log('[useChat] Sending message with conversationId:', conversationId);
      
      // WHY: Validate files is array before using (defensive programming)
      const filesToSend = Array.isArray(selectedImages) ? selectedImages : [];
      
      // Create user message for UI display
      const userMessage = { 
        role: MESSAGE_ROLES.USER, 
        content: message || '(File only message)',
        files: filesToSend.map(file => ({
          preview: file?.preview || null,
          name: file?.name || 'file',
          size: file?.size || 0,
          category: file?.category || 'unknown',
          extension: file?.extension || '',
        })),
      };
      
      // Update chat log with user message
      setChatLog(prev => [...prev, userMessage]);
      const currentMessage = message;
      setMessage('');
      
      // WHY: Wrapped in try-catch to prevent crash if clearImages fails
      try {
        clearImages();
      } catch (err) {
        console.error('Error clearing images:', err);
      }
      
      let response;
      
      // Try agent-specific API first if provided
      if (agentApi && typeof agentApi.sendMessage === 'function') {
        try {
          const result = await agentApi.sendMessage(currentMessage, filesToSend, { 
            mode: transmissionMode,
            conversationId  // Uses current value from closure
          });
          // WHY: Validate result structure before using
          if (result && result.success && result.data) {
            response = result.data;
          } else {
            throw new Error(result?.error || 'Agent API returned unsuccessful response');
          }
        } catch (agentError) {
          // WHY: Fallback to default API if agent-specific fails
          console.error('[useChat] Agent API error, falling back to default:', agentError);
          response = await sendChatMessage(currentMessage, filesToSend, { 
            mode: transmissionMode,
            conversationId  // Uses current value from closure
          });
        }
      } else {
        // Fall back to default chat API with transmission mode and conversation context
        response = await sendChatMessage(currentMessage, filesToSend, { 
          mode: transmissionMode,
          conversationId  // Uses current value from closure
        });
      }
      
      // WHY: Validate response structure before using to prevent crashes
      if (!response) {
        throw new Error('No response received from server');
      }
      
      // CONVERSATION_ID SUPPORT: Update conversationId from response if provided
      // WHY: Backend returns conversation_id after first message (new conversation)
      // This ensures we continue the same conversation for subsequent messages
      if (response.conversation_id) {
        // Only update if different from current
        if (!conversationId || conversationId !== response.conversation_id) {
          // Mark that we just created a conversation to prevent clearing chatLog
          justCreatedConversationRef.current = true;
          setConversationId(response.conversation_id);
          onConversationCreated({
            conversation_id: response.conversation_id,
            title: response.title || 'Untitled Conversation',
            agent_id: response.agent_id,
            created_at: new Date().toLocaleString('sv-SE', { timeZone: 'Asia/Kolkata' }).replace(' ', 'T'),
            updated_at: new Date().toLocaleString('sv-SE', { timeZone: 'Asia/Kolkata' }).replace(' ', 'T'),
            message_count: 1,
            last_message_preview: currentMessage.substring(0, 100)
          });
        }
        
        // Trigger conversation updated callback
        onConversationUpdated({
          conversation_id: response.conversation_id,
          last_message_preview: currentMessage.substring(0, 100),
          updated_at: new Date().toLocaleString('sv-SE', { timeZone: 'Asia/Kolkata' }).replace(' ', 'T'),
          message_count: (response.message_count || 1)
        });
      }
      
      // Support both standard replies and file upload status responses
      // If there's a reply (whether from agent or generated from status), use it
      let replyContent = response.reply;
      
      if (typeof replyContent !== 'string') {
        // If no reply, but we have upload status, generate a user-friendly message
        if (response.upload_status && response.file_id) {
          const statusMessages = {
            'PENDING_S3': `✓ File received and saved to database. Uploading to storage...`,
            'READY': `✓ File successfully uploaded and ready for analysis!`,
            'FAILED': `✗ Failed to upload file. Please try again.`,
          };
          replyContent = statusMessages[response.upload_status] || `Upload status: ${response.upload_status}`;
        } else {
          throw new Error('Invalid response format from server');
        }
      }
      
      // Append agent response to chat log
      const agentMessage = {
        role: MESSAGE_ROLES.AGENT,
        content: replyContent,
        uploadStatus: response.upload_status, // Include for styling if needed
        fileId: response.file_id,
        conversation_id: response.conversation_id || conversationId, // Include for reference
        actions: response.actions || [],       // Interactive action buttons from agent
        quick_replies: response.quick_replies || [], // Quick reply suggestions
      };
      setChatLog(prev => [...prev, agentMessage]);
    } catch (error) {
      // WHY: Comprehensive error handling prevents UI crashes and shows user-friendly messages
      console.error('Error during message send:', error);
      
      // Construct appropriate error message based on error type
      let errorContent = ERROR_MESSAGES.SEND_ERROR || 'Failed to send message. Please try again.';
      
      if (error?.response?.data?.message) {
        errorContent = error.response.data.message;
      } else if (error?.message) {
        errorContent = `Error: ${error.message}`;
      }
      
      // Add error message to chat log for user visibility
      const errorMessage = { 
        role: MESSAGE_ROLES.AGENT, 
        content: errorContent,
        isError: true,
      };
      setChatLog(prev => [...prev, errorMessage]);
      setSendError(errorContent);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message, selectedImages, clearImages, agentApi, transmissionMode, onConversationCreated, onConversationUpdated]);
  // NOTE: conversationId intentionally omitted from deps — read via closure to avoid callback thrashing on conversation switches.
  // Including it causes unnecessary callback recreation and would make conversationId always stale

  /**
   * Handle Enter key press (Shift+Enter for new line)
   * WHY: Prevents sending multiple messages while one is loading
   */
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      // WHY: Check isLoading to prevent duplicate sends during API call
      if (!isLoading) {
        sendMessage();
      }
    }
  }, [sendMessage, isLoading]);

  /**
   * Clear send error for dismissible error handling
   */
  const clearSendError = useCallback(() => {
    setSendError(null);
  }, []);

  /**
   * Send a specific text as a message (used for action buttons and quick replies).
   * Bypasses message state — directly sends the provided text.
   */
  const sendQuickMessage = useCallback(async (text) => {
    if (!text?.trim() || isLoading) return;

    try {
      setSendError(null);
      setIsLoading(true);

      const userMessage = { role: MESSAGE_ROLES.USER, content: text, files: [] };
      setChatLog(prev => [...prev, userMessage]);

      const response = await sendChatMessage(text, [], {
        mode: transmissionMode,
        conversationId,
      });

      if (!response) throw new Error('No response received from server');

      if (response.conversation_id) {
        if (!conversationId || conversationId !== response.conversation_id) {
          justCreatedConversationRef.current = true;
          setConversationId(response.conversation_id);
          onConversationCreated({
            conversation_id: response.conversation_id,
            title: response.title || 'Untitled Conversation',
            agent_id: response.agent_id,
            created_at: new Date().toLocaleString('sv-SE', { timeZone: 'Asia/Kolkata' }).replace(' ', 'T'),
            updated_at: new Date().toLocaleString('sv-SE', { timeZone: 'Asia/Kolkata' }).replace(' ', 'T'),
            message_count: 1,
            last_message_preview: text.substring(0, 100),
          });
        }
        onConversationUpdated({
          conversation_id: response.conversation_id,
          last_message_preview: text.substring(0, 100),
          updated_at: new Date().toLocaleString('sv-SE', { timeZone: 'Asia/Kolkata' }).replace(' ', 'T'),
          message_count: response.message_count || 1,
        });
      }

      const replyContent = typeof response.reply === 'string' ? response.reply : 'Received response.';
      const agentMessage = {
        role: MESSAGE_ROLES.AGENT,
        content: replyContent,
        conversation_id: response.conversation_id || conversationId,
        actions: response.actions || [],
        quick_replies: response.quick_replies || [],
      };
      setChatLog(prev => [...prev, agentMessage]);
    } catch (error) {
      console.error('Error during quick message send:', error);
      const errorContent = error?.response?.data?.message || error?.message || 'Failed to send message.';
      setChatLog(prev => [...prev, { role: MESSAGE_ROLES.AGENT, content: errorContent, isError: true }]);
      setSendError(errorContent);
    } finally {
      setIsLoading(false);
    }
    // conversationId omitted: read via closure to avoid callback thrashing on conversation switches (see sendMessage above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, transmissionMode, onConversationCreated, onConversationUpdated]);

  return {
    message,
    setMessage,
    chatLog,
    isLoading,
    sendMessage,
    sendQuickMessage,
    handleKeyDown,
    sendError,
    clearSendError,
    conversationId,
    setConversationId,
  };
};
